import { useCallback, useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { api, formatApiError, fmtMoney } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  Storefront,
  Sparkle,
  Camera,
  PencilSimpleLine,
  CircleNotch,
  CopySimple,
  CheckCircle,
  Warning,
  ArrowsClockwise,
} from "@phosphor-icons/react";

const TABS = [
  { key: "market", label: "Market", icon: Storefront },
  { key: "recs", label: "Strategy", icon: Sparkle },
  { key: "photos", label: "Photos", icon: Camera },
  { key: "listing", label: "Listing", icon: PencilSimpleLine },
];

const PLATFORMS = [
  { key: "ebay", label: "eBay" },
  { key: "facebook_marketplace", label: "Facebook Marketplace" },
  { key: "craigslist", label: "Craigslist" },
  { key: "reverb", label: "Reverb" },
  { key: "chrono24", label: "Chrono24" },
  { key: "local_consignment", label: "Local Consignment (email)" },
];

const CONFIDENCE_COLOR = {
  high: "bg-[#1A3622] text-[#F9F8F6]",
  medium: "bg-[#9C5012] text-[#F9F8F6]",
  low: "bg-[#5C5F58] text-[#F9F8F6]",
};

export default function ResaleDrawer({ open, onOpenChange, assetId, assetName }) {
  const { canWrite } = useAuth();
  const [tab, setTab] = useState("market");
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyKey, setBusyKey] = useState(null); // 'scan' | 'recs' | 'photos' | 'draft'
  const [platform, setPlatform] = useState("ebay");
  const [askingPrice, setAskingPrice] = useState("");

  const hydrate = useCallback(async () => {
    if (!assetId) return;
    setLoading(true);
    try {
      const r = await api.get(`/resale/${assetId}`);
      setDraft(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load resale workspace.");
    } finally {
      setLoading(false);
    }
  }, [assetId]);

  useEffect(() => {
    if (open && assetId) {
      hydrate();
    }
  }, [open, assetId, hydrate]);

  const runScan = async () => {
    if (!canWrite) return toast.error("Read-only on this ledger.");
    setBusyKey("scan");
    try {
      const r = await api.post(`/resale/${assetId}/scan`, {});
      setDraft((d) => ({ ...(d || {}), spectrum: r.data.spectrum, ebay_sample: r.data.ebay_sample, ebay_configured: r.data.ebay_configured }));
      toast.success("Market scan complete.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Scan failed.");
    } finally {
      setBusyKey(null);
    }
  };

  const runRecs = async () => {
    if (!canWrite) return toast.error("Read-only on this ledger.");
    setBusyKey("recs");
    try {
      const r = await api.post(`/resale/${assetId}/recommend`, {});
      setDraft((d) => ({ ...(d || {}), recommendations: r.data.recommendations }));
      toast.success("Strategy ready.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Recommendations failed.");
    } finally {
      setBusyKey(null);
    }
  };

  const runPhotoAudit = async () => {
    if (!canWrite) return toast.error("Read-only on this ledger.");
    setBusyKey("photos");
    try {
      const r = await api.post(`/resale/${assetId}/photo-audit`, {});
      setDraft((d) => ({ ...(d || {}), photo_audit: r.data.photo_audit, photo_count: r.data.photo_count }));
      toast.success("Photo audit complete.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Photo audit failed.");
    } finally {
      setBusyKey(null);
    }
  };

  const runDraft = async () => {
    if (!canWrite) return toast.error("Read-only on this ledger.");
    setBusyKey("draft");
    try {
      const body = { platform };
      const askingNum = parseFloat(askingPrice);
      if (!Number.isNaN(askingNum) && askingNum > 0) body.asking_price = askingNum;
      const r = await api.post(`/resale/${assetId}/draft`, body);
      setDraft((d) => ({
        ...(d || {}),
        listings: { ...((d && d.listings) || {}), [platform]: r.data.listing },
      }));
      toast.success("Listing drafted.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Drafting failed.");
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl flex flex-col p-0 bg-[#F9F8F6]"
        data-testid="resale-drawer"
      >
        <SheetHeader className="px-6 py-5 border-b border-[#E0DDD6] bg-white">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#9C5012] flex items-center justify-center border border-[#7A3D0D] shrink-0">
              <Storefront size={18} weight="regular" color="#F9F8F6" />
            </div>
            <div className="min-w-0">
              <SheetTitle className="font-serif-display text-xl text-[#0A192F] leading-tight">
                Prep for Resale
              </SheetTitle>
              <SheetDescription className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
                {assetName ? `Leo · ${assetName}` : "Leo · turning the ledger outward"}
              </SheetDescription>
            </div>
          </div>
          <div className="mt-4 flex border border-[#E0DDD6] bg-[#F9F8F6]" data-testid="resale-tabs">
            {TABS.map((t) => {
              const Icon = t.icon;
              const isActive = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  data-testid={`resale-tab-${t.key}`}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-[11px] font-mono-ledger uppercase tracking-[0.15em] transition-colors ${
                    isActive
                      ? "bg-[#0A192F] text-[#F9F8F6]"
                      : "text-[#5C5F58] hover:text-[#0A192F]"
                  }`}
                >
                  <Icon size={12} /> {t.label}
                </button>
              );
            })}
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-[#5C5F58]">
              <CircleNotch size={14} className="animate-spin" /> Loading workspace…
            </div>
          )}

          {!loading && tab === "market" && (
            <MarketTab
              draft={draft}
              busy={busyKey === "scan"}
              onRun={runScan}
              canWrite={canWrite}
            />
          )}

          {!loading && tab === "recs" && (
            <RecsTab
              draft={draft}
              busy={busyKey === "recs"}
              onRun={runRecs}
              canWrite={canWrite}
            />
          )}

          {!loading && tab === "photos" && (
            <PhotosTab
              draft={draft}
              busy={busyKey === "photos"}
              onRun={runPhotoAudit}
              canWrite={canWrite}
            />
          )}

          {!loading && tab === "listing" && (
            <ListingTab
              draft={draft}
              busy={busyKey === "draft"}
              platform={platform}
              setPlatform={setPlatform}
              askingPrice={askingPrice}
              setAskingPrice={setAskingPrice}
              onRun={runDraft}
              canWrite={canWrite}
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

/* ------------------------------- Market ------------------------------- */
function MarketTab({ draft, busy, onRun, canWrite }) {
  const spectrum = draft?.spectrum;
  const ebayConfigured = draft?.ebay_configured;

  return (
    <div className="space-y-5" data-testid="resale-market-panel">
      {!ebayConfigured && (
        <div className="bg-[#F4E9D2] border border-[#C5A059] px-4 py-3 text-xs text-[#5C3D08] flex items-start gap-2">
          <Warning size={14} className="mt-0.5 shrink-0" />
          <div>
            <strong>Leo-reasoned estimates.</strong> eBay sold-listings are not yet wired
            in. Targeted Value numbers below are Leo&apos;s best reasoned guesses, not live
            market comps.
          </div>
        </div>
      )}

      {ebayConfigured && (
        <div className="bg-[#F4F2EC] border border-[#E0DDD6] px-4 py-3 text-xs text-[#5C5F58] flex items-start gap-2">
          <Warning size={14} className="mt-0.5 shrink-0" />
          <div>
            <strong>Active eBay asking prices.</strong> Targeted Value uses live Browse
            API listings (what sellers are asking right now). Sold-comp data requires
            eBay&apos;s Marketplace Insights API (separate approval). Asking prices
            typically run higher than final sale prices.
          </div>
        </div>
      )}

      <RunHeader
        title="Resale Spectrum"
        subtitle="Three tiers, from fast-cash to curated buyer."
        busy={busy}
        onRun={onRun}
        canWrite={canWrite}
        hasResult={!!spectrum}
        runLabel="Run market scan"
        rerunLabel="Re-scan"
        testid="resale-run-scan"
      />

      {!spectrum && !busy && (
        <EmptyHint>Press <em>Run market scan</em> to generate Leo&apos;s resale spectrum.</EmptyHint>
      )}

      {spectrum && (
        <>
          <div className="flex items-center gap-2 text-[11px] font-mono-ledger uppercase tracking-[0.2em] text-[#5C5F58]">
            <span>Confidence</span>
            <span className={`px-1.5 py-0.5 ${CONFIDENCE_COLOR[spectrum.confidence] || CONFIDENCE_COLOR.low}`}>
              {spectrum.confidence || "low"}
            </span>
            {typeof spectrum.comparables_used === "number" && (
              <span>· {spectrum.comparables_used} comparables</span>
            )}
          </div>

          {spectrum.summary && (
            <div className="bg-white border border-[#E0DDD6] px-4 py-3 text-sm text-[#0A192F] italic">
              {spectrum.summary}
            </div>
          )}

          <div className="space-y-3" data-testid="resale-tiers">
            {(spectrum.tiers || []).map((t) => (
              <div
                key={t.key}
                className="bg-white border border-[#E0DDD6] px-4 py-3"
                data-testid={`resale-tier-${t.key}`}
              >
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
                    {t.label}
                  </div>
                  <div className="font-serif-display text-2xl text-[#0A192F]">
                    {t.price == null ? "—" : fmtMoney(t.price)}
                    {t.is_estimate && (
                      <span className="ml-2 text-[10px] font-mono-ledger uppercase tracking-[0.15em] text-[#9C5012] align-middle">
                        est.
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-xs text-[#5C5F58] mt-1">
                  {t.platform_hint && (
                    <span className="font-mono-ledger uppercase tracking-[0.15em]">
                      {t.platform_hint}
                    </span>
                  )}
                  {t.platform_hint && t.rationale && <span> · </span>}
                  {t.rationale}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ----------------------- Recommendations / Strategy ----------------------- */
function RecsTab({ draft, busy, onRun, canWrite }) {
  const recs = draft?.recommendations;
  return (
    <div className="space-y-5" data-testid="resale-recs-panel">
      <RunHeader
        title="Maximise Selling Price"
        subtitle="Platform pick, value-add tasks, timing."
        busy={busy}
        onRun={onRun}
        canWrite={canWrite}
        hasResult={!!recs}
        runLabel="Generate recommendations"
        rerunLabel="Regenerate"
        testid="resale-run-recs"
      />

      {!recs && !busy && (
        <EmptyHint>Press <em>Generate recommendations</em> for Leo&apos;s playbook.</EmptyHint>
      )}

      {recs && (
        <>
          {recs.platform && (
            <div className="bg-white border border-[#E0DDD6] px-4 py-3" data-testid="resale-platform-pick">
              <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-1">
                Recommended Platform
              </div>
              <div className="font-serif-display text-2xl text-[#0A192F]">
                {recs.platform.name}
              </div>
              {recs.platform.justification && (
                <div className="text-xs text-[#5C5F58] mt-1">{recs.platform.justification}</div>
              )}
            </div>
          )}

          {recs.value_add && recs.value_add.length > 0 && (
            <div data-testid="resale-value-add">
              <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2">
                Value-Add Tasks · {recs.value_add.length}
              </div>
              <ul className="space-y-2">
                {recs.value_add.map((t, i) => (
                  <li key={i} className="bg-white border border-[#E0DDD6] px-4 py-3">
                    <div className="text-sm text-[#0A192F] font-medium">{t.task}</div>
                    {t.impact && (
                      <div className="text-xs text-[#5C5F58] mt-1">{t.impact}</div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {recs.timing && (
            <div className="bg-[#F4F2EC] border border-[#E0DDD6] px-4 py-3 text-sm text-[#0A192F] italic" data-testid="resale-timing">
              {recs.timing}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* --------------------------------- Photos --------------------------------- */
function PhotosTab({ draft, busy, onRun, canWrite }) {
  const audit = draft?.photo_audit;
  return (
    <div className="space-y-5" data-testid="resale-photos-panel">
      <RunHeader
        title="Photography Audit"
        subtitle="Missing angles a competent listing needs."
        busy={busy}
        onRun={onRun}
        canWrite={canWrite}
        hasResult={!!audit}
        runLabel="Run photo audit"
        rerunLabel="Re-audit"
        testid="resale-run-photo-audit"
      />

      {!audit && !busy && (
        <EmptyHint>Press <em>Run photo audit</em> to see which angles are missing.</EmptyHint>
      )}

      {audit && (
        <>
          <div className="flex items-center gap-2 text-[11px] font-mono-ledger uppercase tracking-[0.2em] text-[#5C5F58]">
            <span>Verdict</span>
            {audit.verdict === "ready" ? (
              <span className="px-1.5 py-0.5 bg-[#1A3622] text-[#F9F8F6] inline-flex items-center gap-1">
                <CheckCircle size={10} weight="fill" /> Ready to list
              </span>
            ) : (
              <span className="px-1.5 py-0.5 bg-[#9C5012] text-[#F9F8F6] inline-flex items-center gap-1">
                <Warning size={10} weight="fill" /> Needs more photos
              </span>
            )}
            {typeof draft?.photo_count === "number" && (
              <span>· {draft.photo_count} attached</span>
            )}
          </div>

          {(audit.missing || []).length === 0 ? (
            <div className="bg-white border border-[#E0DDD6] px-4 py-3 text-sm text-[#0A192F]">
              No missing angles detected. Listing photos look complete.
            </div>
          ) : (
            <ul className="space-y-2" data-testid="resale-photo-gaps">
              {audit.missing.map((m, i) => (
                <li key={i} className="bg-white border border-[#E0DDD6] px-4 py-3" data-testid={`resale-photo-gap-${m.key}`}>
                  <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#9C5012] mb-1">
                    Missing · {m.key}
                  </div>
                  <div className="text-sm text-[#0A192F] font-medium">{m.label}</div>
                  {m.guidance && (
                    <div className="text-xs text-[#5C5F58] mt-1">{m.guidance}</div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

/* -------------------------------- Listing -------------------------------- */
function ListingTab({ draft, busy, platform, setPlatform, askingPrice, setAskingPrice, onRun, canWrite }) {
  const listing = draft?.listings?.[platform];
  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard.");
    } catch {
      toast.error("Copy failed.");
    }
  };

  return (
    <div className="space-y-5" data-testid="resale-listing-panel">
      <div className="bg-white border border-[#E0DDD6] px-4 py-3 space-y-3">
        <div>
          <label className="block font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-1">
            Platform
          </label>
          <select
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
            data-testid="resale-platform-select"
            className="w-full border border-[#E0DDD6] bg-white px-3 py-2 text-sm focus:border-[#1A3622] focus:outline-none"
          >
            {PLATFORMS.map((p) => (
              <option key={p.key} value={p.key}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-1">
            Asking Price (optional)
          </label>
          <input
            type="number"
            inputMode="decimal"
            placeholder="e.g. 1250"
            value={askingPrice}
            onChange={(e) => setAskingPrice(e.target.value)}
            data-testid="resale-asking-price"
            className="w-full border border-[#E0DDD6] bg-white px-3 py-2 text-sm focus:border-[#1A3622] focus:outline-none"
          />
        </div>
        <button
          onClick={onRun}
          disabled={busy || !canWrite}
          data-testid="resale-run-draft"
          className="w-full bg-[#1A3622] text-[#F9F8F6] py-2.5 text-sm font-medium border border-[#0F2818] hover:bg-[#0F2818] disabled:opacity-50 inline-flex items-center justify-center gap-2"
        >
          {busy ? (
            <>
              <CircleNotch size={14} className="animate-spin" /> Drafting…
            </>
          ) : (
            <>
              <PencilSimpleLine size={14} /> {listing ? "Re-draft listing" : "Draft listing"}
            </>
          )}
        </button>
      </div>

      {!listing && !busy && (
        <EmptyHint>Pick a platform and press <em>Draft listing</em>. Leo will write a platform-tailored draft with an As-Is guardrail.</EmptyHint>
      )}

      {listing && (
        <div className="bg-white border border-[#E0DDD6] divide-y divide-[#E0DDD6]" data-testid="resale-listing-output">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between gap-2">
              <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
                Title
              </div>
              <button
                onClick={() => copy(listing.title || "")}
                data-testid="resale-copy-title"
                className="text-[10px] font-mono-ledger uppercase tracking-[0.2em] text-[#5C5F58] hover:text-[#1A3622] inline-flex items-center gap-1"
              >
                <CopySimple size={11} /> Copy
              </button>
            </div>
            <div className="font-serif-display text-lg text-[#0A192F] mt-1">{listing.title}</div>
          </div>
          <div className="px-4 py-3">
            <div className="flex items-center justify-between gap-2">
              <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
                Body
              </div>
              <button
                onClick={() => copy(listing.body || "")}
                data-testid="resale-copy-body"
                className="text-[10px] font-mono-ledger uppercase tracking-[0.2em] text-[#5C5F58] hover:text-[#1A3622] inline-flex items-center gap-1"
              >
                <CopySimple size={11} /> Copy
              </button>
            </div>
            <div className="prose prose-sm max-w-none text-[#0A192F] mt-2 [&>*:first-child]:mt-0">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{listing.body || ""}</ReactMarkdown>
            </div>
          </div>
          {(listing.tags || []).length > 0 && (
            <div className="px-4 py-3">
              <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2">
                Tags
              </div>
              <div className="flex flex-wrap gap-1.5">
                {listing.tags.map((t, i) => (
                  <span
                    key={i}
                    className="text-[11px] font-mono-ledger uppercase tracking-[0.1em] bg-[#F4F2EC] border border-[#E0DDD6] px-2 py-0.5 text-[#5C5F58]"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}
          {listing.as_is_clause && (
            <div className="px-4 py-3 bg-[#F4E9D2]/40">
              <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C3D08] mb-1">
                As-Is Guardrail
              </div>
              <div className="text-xs text-[#0A192F] italic">{listing.as_is_clause}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------------------------- shared sub-pieces ---------------------------- */
function RunHeader({ title, subtitle, busy, onRun, canWrite, hasResult, runLabel, rerunLabel, testid }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="font-serif-display text-xl text-[#0A192F] leading-tight">{title}</div>
        <div className="text-xs text-[#5C5F58] mt-0.5">{subtitle}</div>
      </div>
      <button
        onClick={onRun}
        disabled={busy || !canWrite}
        data-testid={testid}
        className="shrink-0 inline-flex items-center gap-2 px-3 py-2 text-xs border border-[#1A3622] bg-[#1A3622] text-[#F9F8F6] hover:bg-[#0F2818] disabled:opacity-50"
        title={!canWrite ? "Read-only role — cannot run Leo on this ledger" : ""}
      >
        {busy ? (
          <>
            <CircleNotch size={12} className="animate-spin" /> Running…
          </>
        ) : hasResult ? (
          <>
            <ArrowsClockwise size={12} /> {rerunLabel}
          </>
        ) : (
          <>
            <Sparkle size={12} /> {runLabel}
          </>
        )}
      </button>
    </div>
  );
}

function EmptyHint({ children }) {
  return (
    <div className="bg-white border border-dashed border-[#E0DDD6] px-4 py-6 text-center text-sm text-[#5C5F58]">
      {children}
    </div>
  );
}
